import { siteConfig } from '../config/siteConfig';
import { PageHero } from "./PageHero";
import { useState } from 'react';
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Mark } from "./Mark";
import {
  Mail,
  Phone,
  CheckCircle,
  Clock,
  AlertTriangle,
  Zap,
  Loader2,
  AlertCircle
} from "lucide-react";
import { submitSupportTicket, ApiError } from "../config/api";

interface ContactSupportPageProps {
  onNavigate: (page: string) => void;
}

export function ContactSupportPage({ onNavigate }: ContactSupportPageProps) {
  const [ticketData, setTicketData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    category: '',
    priority: '',
    subject: '',
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateField = (field: string, value: string): string | undefined => {
    const trimmed = value ? value.trim() : '';
    switch (field) {
      case 'name':
        if (!trimmed) return 'Enter your name.';
        return undefined;
      case 'email':
        if (!trimmed) return 'Enter your work email.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Enter an email address like name@company.com.';
        return undefined;
      case 'company':
        if (!trimmed) return 'Enter your company name or tenant ID.';
        return undefined;
      case 'phone':
        if (!trimmed) return undefined;
        if (!/^\+?[\d\s\-]+$/.test(trimmed)) {
          return 'Enter a phone number with country code, for example +91 80 4663 2400.';
        }
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length < 6 || digits.length > 16) {
          return 'Enter a phone number with country code, for example +91 80 4663 2400.';
        }
        return undefined;
      case 'category':
        if (!trimmed) return 'Select a category.';
        return undefined;
      case 'priority':
        if (!trimmed) return 'Select a priority.';
        return undefined;
      case 'subject':
        if (trimmed.length < 5) return "Add a few words so we know what it's about.";
        return undefined;
      case 'description':
        if (trimmed.length < 10) return 'Tell us what happened, in a sentence or two.';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (field: string) => {
    const error = validateField(field, ticketData[field as keyof typeof ticketData] || '');
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  const handleInputChange = (field: string, value: string) => {
    setTicketData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      const error = validateField(field, value);
      setErrors(prev => ({ ...prev, [field]: error }));
    }
    if (apiError) setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError(null);

    const fieldsToValidate = ['name', 'email', 'company', 'phone', 'category', 'priority', 'subject', 'description'];
    const newErrors: Record<string, string | undefined> = {};
    let hasError = false;

    fieldsToValidate.forEach((field) => {
      const err = validateField(field, ticketData[field as keyof typeof ticketData] || '');
      if (err) {
        newErrors[field] = err;
        hasError = true;
      }
    });

    setErrors(newErrors);

    if (hasError) return;

    setIsLoading(true);
    try {
      const res = await submitSupportTicket(ticketData);
      setTicketNumber(res?.data?.ticketNumber ?? null);
      setIsSubmitted(true);
    } catch (error: any) {
      if (error?.errors && Array.isArray(error.errors) && error.errors.length > 0) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach((err: any) => {
          const path = err.path || err.param;
          if (path && !fieldErrors[path]) {
            fieldErrors[path] = err.msg;
          }
        });
        setErrors(prev => ({ ...prev, ...fieldErrors }));
      }
      // Server errors carry a message worth showing; a network failure ("Failed to fetch") doesn't.
      setApiError(error instanceof ApiError
        ? error.message
        : `We couldn't send this. Check your connection and try again, or email ${siteConfig.contact.support}.`);
    } finally {
      setIsLoading(false);
    }
  };

  const getSeverityIcon = (priority: string) => {
    switch (priority) {
      case 'urgent': return <Zap className="w-4 h-4 text-royal-indigo" />;
      case 'high': return <AlertTriangle className="w-4 h-4 text-royal-indigo" />;
      case 'medium': return <Clock className="w-4 h-4 text-indigo-tint-20" />;
      case 'low': return <CheckCircle className="w-4 h-4 text-indigo-tint-20" />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        title={<>Get help with <Mark>TurfAI</Mark></>}
        lede="For customers using TurfAI or its accelerators. New to TurfAI? Schedule a demo instead."
      />


      {/* Support Channels */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Email or <Mark>call</Mark></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
            <Card className="p-6 border-softblack-tint-20 transition-shadow">
              <CardContent className="p-0">
                <div className="w-12 h-12 bg-indigo-tint-20 rounded-lg flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-royal-indigo" />
                </div>
                <h3 className="text-[22px] font-semibold text-dark-indigo mb-2">Email</h3>
                <p className="text-sm text-soft-black mb-4">
                  {siteConfig.contact.support}
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a href={`mailto:${siteConfig.contact.support}`}>Email support</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="p-6 border-softblack-tint-20 transition-shadow">
              <CardContent className="p-0">
                <div className="w-12 h-12 bg-indigo-tint-20 rounded-lg flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6 text-royal-indigo" />
                </div>
                <h3 className="text-[22px] font-semibold text-dark-indigo mb-2">Phone</h3>
                <p className="text-sm text-soft-black mb-4">
                  {siteConfig.contact.phone}
                </p>
                <Button variant="outline" size="sm" asChild>
                  <a href={siteConfig.contact.phoneHref}>Call</a>
                </Button>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Submit Ticket Form */}
      <section className="py-20 lg:py-24 band-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-[36px] lg:text-[42px] leading-tight font-bold tracking-tight text-dark-indigo mb-4">Raise a <Mark>ticket</Mark></h2>
              <p className="text-[22px] text-soft-black">
                Tell us what's wrong and we'll reply by email.
              </p>
            </div>

            {!isSubmitted ? (
              <Card className="p-8 bg-white border-softblack-tint-20">
                <CardContent className="p-0">
                  <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        required
                        value={ticketData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        onBlur={() => handleBlur('name')}
                        aria-invalid={!!errors.name}
                      />
                      {errors.name && <div className="text-danger-text text-xs mt-1">{errors.name}</div>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Work email</Label>
                        <Input 
                          id="email"
                          type="email"
                          required
                          value={ticketData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          onBlur={() => handleBlur('email')}
                          aria-invalid={!!errors.email}
                        />
                        {errors.email && <div className="text-danger-text text-xs mt-1">{errors.email}</div>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone (optional)</Label>
                        <Input 
                          id="phone"
                          type="tel"
                          placeholder="Include the country code"
                          value={ticketData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          onBlur={() => handleBlur('phone')}
                          aria-invalid={!!errors.phone}
                        />
                        {errors.phone && <div className="text-danger-text text-xs mt-1">{errors.phone}</div>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company or tenant ID</Label>
                      <Input 
                        id="company"
                        required
                        placeholder="Company name or tenant identifier"
                        value={ticketData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        onBlur={() => handleBlur('company')}
                        aria-invalid={!!errors.company}
                      />
                      {errors.company && <div className="text-danger-text text-xs mt-1">{errors.company}</div>}
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="category">Category</Label>
                        <Select value={ticketData.category} onValueChange={(value) => handleInputChange('category', value)}>
                          <SelectTrigger id="category">
                            <SelectValue placeholder="Choose a category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="technical">Technical</SelectItem>
                            <SelectItem value="billing">Billing and account</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="feature_request">Feature request</SelectItem>
                            <SelectItem value="bug_report">Bug report</SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.category && <div className="text-danger-text text-xs mt-1">{errors.category}</div>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority</Label>
                        <Select value={ticketData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                          <SelectTrigger id="priority">
                            <SelectValue placeholder="Choose a priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">
                              <div className="flex items-center gap-2">
                                {getSeverityIcon('low')}
                                Low — a question or a minor issue
                              </div>
                            </SelectItem>
                            <SelectItem value="medium">
                              <div className="flex items-center gap-2">
                                {getSeverityIcon('medium')}
                                Medium — something's broken, with a workaround
                              </div>
                            </SelectItem>
                            <SelectItem value="high">
                              <div className="flex items-center gap-2">
                                {getSeverityIcon('high')}
                                High — a key workflow is blocked
                              </div>
                            </SelectItem>
                            <SelectItem value="urgent">
                              <div className="flex items-center gap-2">
                                {getSeverityIcon('urgent')}
                                Urgent — production is down
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        {errors.priority && <div className="text-danger-text text-xs mt-1">{errors.priority}</div>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input 
                        id="subject"
                        required
                        placeholder="One line on the problem"
                        value={ticketData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        onBlur={() => handleBlur('subject')}
                        aria-invalid={!!errors.subject}
                      />
                      {errors.subject && <div className="text-danger-text text-xs mt-1">{errors.subject}</div>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea 
                        id="description"
                        required
                        placeholder="What happened, what you expected, and any error message."
                        className="min-h-[120px]"
                        value={ticketData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        onBlur={() => handleBlur('description')}
                        aria-invalid={!!errors.description}
                      />
                      {errors.description && <div className="text-danger-text text-xs mt-1">{errors.description}</div>}
                    </div>

                    {apiError && (
                      <div className="flex items-start gap-2 p-4 bg-orange-tint-20 border border-orange-tint-20 rounded-lg text-danger-text">
                        <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <p className="text-sm">{apiError}</p>
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            ) : (
              <Card className="p-8 bg-white border-softblack-tint-20">
                <CardContent className="p-0 text-center">
                  <div className="w-16 h-16 bg-indigo-tint-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-royal-indigo" />
                  </div>
                  <h3 className="text-[22px] lg:text-[24px] font-semibold text-dark-indigo mb-4">{ticketNumber ? <>Ticket <span className="font-mono">{ticketNumber}</span> received</> : 'Ticket received'}</h3>
                  <p className="text-soft-black mb-6">
                    We've emailed you a copy. Reply to that email to add details.
                  </p>
                  <Button onClick={() => setIsSubmitted(false)} variant="outline">
                    Raise another ticket
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}